import { useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronUp, Columns3, X } from "lucide-react";
import {
  VIEW_COLUMN_LABEL,
  reconcileColumns,
  type ViewEntityType,
  type ViewColumnSpec,
} from "@colab/shared";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

// Phase 7 sub-bucket 17 — SPA port of components/shared/views/ConfigureColumns.tsx
// (173 LOC legacy). Column visibility + reordering popover. Reorder uses
// up/down arrows (legacy parity); a future iteration can swap to @dnd-kit
// vertical sortable when there's a clear ergonomic win — for short column
// lists (5-10 items) the arrow controls are arguably faster anyway.
//
// `vendor-dnd-kit` chunk stays flat — no new dnd-kit consumer this port.

type Props = {
  entityType: ViewEntityType;
  value: ViewColumnSpec[];
  onSave: (next: ViewColumnSpec[]) => void;
  /** Externally-controlled open state for parent-driven menu entries. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  disabled?: boolean;
};

export function ConfigureColumns({
  entityType,
  value,
  onSave,
  open: controlledOpen,
  onOpenChange,
  disabled = false,
}: Props) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const open = controlledOpen ?? uncontrolledOpen;
  const setOpen = onOpenChange ?? setUncontrolledOpen;

  // Reconcile the incoming value against the catalog every time the popover
  // opens so stale columns disappear and brand-new ones show up.
  const initial = useMemo(
    () => reconcileColumns(value, entityType),
    [value, entityType],
  );
  const [draft, setDraft] = useState<ViewColumnSpec[]>(initial);

  useEffect(() => {
    if (open) setDraft(initial);
  }, [open, initial]);

  const visibleCount = draft.filter((c) => c.visible).length;

  function toggleVisible(key: string, next: boolean) {
    setDraft((prev) =>
      prev.map((c) => (c.key === key ? { ...c, visible: next } : c)),
    );
  }

  function move(key: string, direction: -1 | 1) {
    setDraft((prev) => {
      const idx = prev.findIndex((c) => c.key === key);
      if (idx < 0) return prev;
      const swapIdx = idx + direction;
      if (swapIdx < 0 || swapIdx >= prev.length) return prev;
      const next = prev.slice();
      [next[idx], next[swapIdx]] = [next[swapIdx], next[idx]];
      return next;
    });
  }

  function commit() {
    onSave(draft);
    setOpen(false);
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          disabled={disabled}
          className="h-8 gap-1.5 px-2.5 text-xs"
        >
          <Columns3 className="h-3.5 w-3.5" />
          Columns
          <span className="text-[10px] tabular-nums text-muted-foreground">
            ({visibleCount})
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-3">
        <div className="mb-2 flex items-center justify-between">
          <h4 className="font-display text-sm font-semibold tracking-tight">
            Columns
          </h4>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
            aria-label="Close"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
        <p className="mb-3 text-xs text-muted-foreground">
          Toggle visibility, reorder with the arrows.
        </p>

        <ul className="space-y-1">
          {draft.map((col, idx) => (
            <li
              key={col.key}
              className={cn(
                "group flex items-center gap-2 rounded-md border bg-card/30 px-2 py-1.5 text-xs",
                !col.visible && "opacity-60",
              )}
            >
              <Checkbox
                id={`col-${col.key}`}
                checked={col.visible}
                onCheckedChange={(c) => toggleVisible(col.key, c === true)}
                aria-label={`Show ${VIEW_COLUMN_LABEL[col.key] ?? col.key}`}
              />
              <label
                htmlFor={`col-${col.key}`}
                className="flex-1 cursor-pointer truncate font-medium"
              >
                {VIEW_COLUMN_LABEL[col.key] ?? col.key}
              </label>
              <div className="flex items-center gap-0.5 opacity-60 transition-opacity group-hover:opacity-100">
                <button
                  type="button"
                  onClick={() => move(col.key, -1)}
                  disabled={idx === 0}
                  aria-label="Move up"
                  className="rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <ChevronUp className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => move(col.key, 1)}
                  disabled={idx === draft.length - 1}
                  aria-label="Move down"
                  className="rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <ChevronDown className="h-3.5 w-3.5" />
                </button>
              </div>
            </li>
          ))}
        </ul>

        <div className="mt-3 flex items-center justify-end gap-2 border-t pt-3">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setOpen(false)}
          >
            Cancel
          </Button>
          <Button type="button" size="sm" onClick={commit}>
            Apply
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
